import { useCallback, useEffect, useMemo, useState } from "react";
import { trackEvent } from "@hopper-b2b/api";
import { getPercentage, I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import {
  CfarTrackingEvents,
  CfarTrackingPageSelected,
  ChfarTrackingEvents,
  ChfarTrackingPageSelected,
  DisruptionTrackingEvents,
  type FareDetails,
  type FiatPrice,
  type FintechProductOption,
  FintechProductType,
} from "@hopper-b2b/types";
import {
  FintechPurchaseCard,
  LearnMore,
  MobileFloatingSection,
  TermsAndConditions,
} from "@hopper-b2b/ui";
import { useEnableFintechPurchaseErrorState } from "@hopper-b2b/utilities";
import clsx from "clsx";
import AirplaneDepart from "../../../assets/client/airplane-depart.svg?react";
import Arrows from "../../../assets/client/arrows.svg?react";
import Card from "../../../assets/client/card-fintech.svg?react";
import Checkmark from "../../../assets/client/checkmark.svg?react";
import Clock from "../../../assets/client/clock-simple-icon.svg?react";
import Headset from "../../../assets/client/headset.svg?react";
import styles from "./styles.module.scss";

export interface IFintechPurchaseProps {
  product: FintechProductType;
  options: FintechProductOption[];
  headerTitle?: string;
  headerSubtitle: string | JSX.Element;
  ctaLabel: string;
  handleContinue?: () => void;
  className?: string;
  clientAssets?: Record<string, any>;
  modal?: boolean;
  disabled?: boolean;
  loading?: boolean;
  isHalfSheet?: boolean;
  validUntilDate?: string;
  fareDetails: FareDetails;
}

export const FintechPurchase = ({
  product,
  options,
  headerTitle,
  headerSubtitle,
  ctaLabel,
  handleContinue,
  className,
  clientAssets,
  modal = false,
  disabled = false,
  loading = false,
  isHalfSheet = false,
  validUntilDate,
  fareDetails,
}: IFintechPurchaseProps) => {
  const { t, locale, formatFiatCurrency } = useI18nContext();
  const enableFintechPurchaseErrorState = useEnableFintechPurchaseErrorState();
  const [showErrorState, setShowErrorState] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const pricing = fareDetails?.paxPricings[0].pricing;
  const totalCostFiat: FiatPrice = useMemo(() => {
    return {
      value:
        (pricing?.baseAmount?.fiat.value || 0) +
        (pricing?.taxAmount?.fiat.value || 0) +
        (pricing?.additionalMargin ? pricing.additionalMargin.fiat.value : 0),
      currencyCode: pricing?.baseAmount.fiat.currencyCode,
      currencySymbol: pricing?.baseAmount.fiat.currencySymbol,
    };
  }, [pricing]);

  const ctaLabelFinal = useMemo(() => {
    if (product === FintechProductType.CfarExercise) {
      const selectedOption = options.find((option) => option.selected);
      if (
        selectedOption?.value === "full" ||
        selectedOption?.value === "partial"
      ) {
        return `${t("percentageRefundable", {
          percentage: getPercentage(
            selectedOption?.value === "partial" ? 80 : 100,
            locale
          ),
        })} | ${selectedOption?.cost}`;
      }
      return ctaLabel;
    }
    return ctaLabel;
  }, [options, product, ctaLabel, t, locale]);

  const title = useMemo(() => {
    switch (product) {
      case FintechProductType.Disruption:
        return t("disruptionProtection.header");
      default:
        return headerTitle;
    }
  }, [product, headerTitle, t]);

  const subtitle = useMemo(() => {
    switch (product) {
      case FintechProductType.Disruption:
        return t("disruptionPurchase.subheader");
      default:
        return headerSubtitle;
    }
  }, [product, headerSubtitle, t]);

  const refundAmount = useMemo(() => {
    if (product !== FintechProductType.CfarExercise) return;

    const selectedOption = options.find((option) => option.selected);
    const formatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    if (selectedOption?.value === "partial") {
      const value = totalCostFiat.value * 0.8;
      return formatFiatCurrency({ ...totalCostFiat, value }, formatOptions);
    }

    if (selectedOption?.value === "full") {
      return formatFiatCurrency(totalCostFiat, formatOptions);
    }
  }, [product, options, totalCostFiat, formatFiatCurrency]);

  const refundAmountPercentage = useMemo(() => {
    let percentage;
    const selectedOption = options.find((option) => option.selected);
    if (product === FintechProductType.CfarExercise) {
      if (selectedOption?.value === "partial") {
        percentage = 80;
      } else if (selectedOption?.value === "full") {
        percentage = 100;
      }
    }
    return getPercentage(percentage, locale);
  }, [product, locale, options]);

  const refundAmountString = useMemo(() => {
    const selectedOption = options.find((option) => option.selected);

    switch (selectedOption?.value) {
      case "partial":
      case "full":
        return t("cfarOffers.valueProps.second.selected", {
          percentage: refundAmountPercentage,
          refundAmount: refundAmount,
        });
      default:
        return t("cfarOffers.valueProps.second.none");
    }
  }, [refundAmount, t, options, refundAmountPercentage]);

  const valueProps = useMemo(() => {
    switch (product) {
      case FintechProductType.CfarExercise:
        return [
          {
            icon: <Clock />,
            content: t("cfarOffers.valueProps.first", {
              timeFrame: validUntilDate,
            }),
          },
          {
            icon: <Card />,
            content: refundAmountString,
          },
          {
            icon: <Checkmark />,
            content: t("cfarOffers.valueProps.third"),
          },
        ];
      case FintechProductType.Chfar:
        return [
          {
            icon: <Arrows />,
            content: t("chOffer.valueProps.first"),
          },
          {
            icon: <Card />,
            content: t("chOffer.valueProps.second"),
          },
          {
            icon: <Clock />,
            content: t("chOffer.valueProps.third"),
          },
        ];
      case FintechProductType.Disruption:
        return [
          {
            icon: <AirplaneDepart />,
            content: t("disruptionPurchase.valueProps.first"),
          },
          {
            icon: <Card />,
            content: t("disruptionPurchase.valueProps.second"),
          },
          {
            icon: <Headset />,
            content: t("disruptionPurchase.valueProps.third"),
          },
        ];
      default:
        return null;
    }
  }, [product, refundAmountString, t, validUntilDate]);

  const selectionSubtitle = useMemo(() => {
    switch (product) {
      case FintechProductType.CfarExercise:
        return t("cfarOffers.selectSubtitle");
      default:
        return null;
    }
  }, [product, t]);

  // Cfar Learn more within Fintech Selection Page
  const onOpenCfarLearnMore = useCallback(() => {
    trackEvent({
      eventName: CfarTrackingEvents.TAP_LEARN_MORE,
      properties: { page_selected: CfarTrackingPageSelected.OFFER },
    });
  }, []);

  const noneSelected = useMemo(
    () => !options.some((option) => option.selected),
    [options]
  );

  const handleContinueOnClick = useCallback(() => {
    if (enableFintechPurchaseErrorState && noneSelected) {
      setShowErrorState(true);
    } else {
      handleContinue();
    }
  }, [enableFintechPurchaseErrorState, noneSelected, handleContinue]);

  useEffect(() => {
    if (!noneSelected) {
      setShowErrorState(false);
    }
  }, [noneSelected]);

  const handleOpenTerms = useCallback(() => {
    setOpenTerms(true);
    switch (product) {
      case FintechProductType.CfarPurchase:
      case FintechProductType.CfarExercise:
        trackEvent({
          eventName: CfarTrackingEvents.TAP_TERMS_CONDITIONS,
          properties: {
            page_selected: FintechProductType.CfarPurchase
              ? CfarTrackingPageSelected.OFFER
              : CfarTrackingPageSelected.EXERCISE,
          },
        });
        break;
      case FintechProductType.Disruption:
        trackEvent({
          eventName: DisruptionTrackingEvents.TAP_DELAY_TERMS,
          properties: undefined,
        });
        break;
      case FintechProductType.Chfar:
        trackEvent({
          eventName: ChfarTrackingEvents.TAP_TERMS_CONDITIONS,
          properties: {
            page_selected: ChfarTrackingPageSelected.OFFER,
          },
        });
        break;
    }
  }, [product]);

  return (
    <div
      className={clsx("fintech-purchase", styles.fintechPurchase, className, {
        modal: modal || isHalfSheet,
        "half-sheet": isHalfSheet,
      })}
    >
      <div className="fintech-purchase-content">
        <div className="fintech-purchase-header">
          <div>
            {title && <h2 className="fintech-purchase-title">{title}</h2>}
            {product === FintechProductType.CfarExercise ? null : (
              <div className="fintech-purchase-subtitle">
                <p className="fintech-purchase-subtitle-content">{subtitle}</p>
              </div>
            )}
            {valueProps ? (
              <div className="fintech-purchase-value-props">
                {valueProps.map((valueProp) => {
                  return (
                    <div className="fintech-purchase-value-prop">
                      <div className="icon">{valueProp.icon}</div>
                      <div className="content">{valueProp.content}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="fintech-purchase-learn-more">
              {isHalfSheet && (
                <LearnMore
                  label={t("learnMore")}
                  description={
                    <I18nMarkup tKey={"cfarOffers.learnMoreDescription"} />
                  }
                  productType={FintechProductType.CfarPurchase}
                  descriptionAdditional
                  header={t("cfarOffers.learnMoreHeader")}
                  title={t("cfarOffers.learnMoreTitle")}
                  className="cfar-learn-more"
                  onOpen={onOpenCfarLearnMore}
                />
              )}
            </div>
          </div>
        </div>
        <div className="fintech-purchase-card-section">
          <div className="fintech-purchase-card-section-header">
            <h3 className="fintech-purchase-card-section-title">
              {t("selectCoverageType")}
            </h3>
            {showErrorState ? (
              <div className="fintech-purchase-card-error">
                {t("requiredField")}
              </div>
            ) : null}
          </div>
          {selectionSubtitle ? (
            <div className="fintech-purchase-card-section-subheader">
              <p className="fintech-purchase-card-section-subtitle">
                {selectionSubtitle}
              </p>
            </div>
          ) : null}
          {options.map((option: FintechProductOption) => (
            <FintechPurchaseCard
              isHalfSheet={isHalfSheet}
              key={option.value}
              option={option}
              className={className}
              clientAssets={clientAssets}
              loading={loading}
            />
          ))}
          <button
            className="fintech-purchase-card-terms"
            onClick={handleOpenTerms}
          >
            {t("termsAndConditions")}
          </button>
        </div>
      </div>
      {handleContinue ? (
        <MobileFloatingSection
          className="fintech-purchase-floating-section fintech-floating-section"
          primaryButton={{
            children: ctaLabelFinal,
            onClick: handleContinueOnClick,
            className: "fintech-purchase-button accept",
            wrapperClassName: "fintech-purchase-button-container",
            disabled: enableFintechPurchaseErrorState ? false : disabled,
          }}
        />
      ) : null}
      {!!product && (
        <TermsAndConditions
          productType={product}
          open={openTerms}
          onClose={() => setOpenTerms(false)}
        />
      )}
    </div>
  );
};
